import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastController, ModalController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { Network } from '@capacitor/network';
import { firstValueFrom } from 'rxjs';
import {
  QueuedAction,
  TicketScan,
  CountSyncResponse,
  TicketSyncResponse,
  EventCountsResponse,
  EventTicketsResponse,
  DeletionResponse,
  ConflictDeviceScan,
  TicketConflict,
  QrMappingsResponse,
  QrMappingProofResponse,
  QrAccessRequestResponse
} from '../models/server-sync.models';
import { ConflictsModalComponent } from '../components/conflicts-modal.component';
import { DeviceService } from './device.service';
import { StorageService } from './storage.service';
import { DebugLoggerService } from './debug-logger.service';
import { CustomQRTemplate } from '../models/event.model';
import { FeatureGateService } from './feature-gate.service';

// Server URL - update this to your production server
// Use port 8080 to reach the hybrid server with storage/metadata
const SERVER_URL = 'https://ws.aforo.bink.tools:8080';
// For local testing:
// const SERVER_URL = 'http://localhost:3000';

type NetworkType = 'wifi' | 'cellular' | 'none' | 'unknown';
type NetworkEffectiveType = '2g' | '3g' | '4g' | '5g' | 'unknown';

interface NetworkContextPayload {
  type: NetworkType;
  effectiveType?: NetworkEffectiveType;
  capturedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ServerSyncService {
  private http = inject(HttpClient);
  private deviceService = inject(DeviceService);
  private storage = inject(StorageService);
  private toastController = inject(ToastController);
  private modalController = inject(ModalController);
  private translateService = inject(TranslateService);
  private debugLogger = inject(DebugLoggerService);
  private featureGate = inject(FeatureGateService);

  private readonly QUEUE_KEY = 'server_sync_queue';
  private readonly MAX_QUEUE_SIZE = 1000;
  private readonly COUNT_SYNC_DEBOUNCE_MS = 1500;

  private syncQueue = signal<QueuedAction[]>([]);
  private isSyncing = signal<boolean>(false);
  private deviceId?: string;
  private countSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private pendingCountSyncs = new Map<string, {
    deviceCount: number;
    totalEstimate: number;
    eventName?: string;
    eventPassword?: string;
    isCreator?: boolean;
    resolve: (value: void) => void;
    reject: (reason?: unknown) => void;
  }>();

  constructor() {
    this.initializeDeviceId();
    this.loadQueue();
  }

  private async ensureServerSyncAccess(operationName: string): Promise<boolean> {
    if (this.featureGate.canUseCached('serverSync') === true) {
      return true;
    }

    const allowed = await this.featureGate.canUse('serverSync');
    if (!allowed) {
      this.debugLogger.warn('ServerSync', `Blocked ${operationName} (Complete plan required)`);
    }
    return allowed;
  }

  private async initializeDeviceId() {
    const deviceInfo = await this.deviceService.getDeviceInfo();
    this.deviceId = deviceInfo.deviceId;
  }

  /**
   * Load queue from storage
   */
  private async loadQueue() {
    try {
      const queueJson = await this.storage.get(this.QUEUE_KEY);
      if (queueJson) {
        const queue = JSON.parse(queueJson);
        this.syncQueue.set(queue);
      }
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to load queue:', error);
    }
  }

  /**
   * Save queue to storage
   */
  private async saveQueue() {
    try {
      const queue = this.syncQueue();
      await this.storage.set(this.QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to save queue:', error);
    }
  }

  /**
   * Add action to queue
   */
  private async queueAction(action: QueuedAction) {
    const queue = this.syncQueue();
    
    // Limit queue size
    if (queue.length >= this.MAX_QUEUE_SIZE) {
      queue.shift();
    }
    
    queue.push(action);
    this.syncQueue.set([...queue]);
    await this.saveQueue();
    
  }

  private async getNetworkContext(): Promise<NetworkContextPayload> {
    const capturedAt = Date.now();
    let type: NetworkType = 'unknown';
    let effectiveType: NetworkEffectiveType | undefined;

    try {
      const status = await Network.getStatus();
      if (status.connectionType === 'wifi' || status.connectionType === 'cellular' || status.connectionType === 'none' || status.connectionType === 'unknown') {
        type = status.connectionType;
      }
    } catch (error) {
      this.debugLogger.warn('ServerSync', 'Failed to read network status; defaulting to unknown.', error);
    }

    try {
      const browserConnection = (navigator as Navigator & {
        connection?: { effectiveType?: string };
        mozConnection?: { effectiveType?: string };
        webkitConnection?: { effectiveType?: string };
      }).connection
        || (navigator as Navigator & { mozConnection?: { effectiveType?: string } }).mozConnection
        || (navigator as Navigator & { webkitConnection?: { effectiveType?: string } }).webkitConnection;

      const rawEffectiveType = String(browserConnection?.effectiveType || '').trim().toLowerCase();
      if (rawEffectiveType === '2g' || rawEffectiveType === '3g' || rawEffectiveType === '4g' || rawEffectiveType === '5g') {
        effectiveType = rawEffectiveType;
      } else if (rawEffectiveType) {
        effectiveType = 'unknown';
      }
    } catch (error) {
      this.debugLogger.warn('ServerSync', 'Failed to read effective network type.', error);
    }

    return effectiveType
      ? { type, effectiveType, capturedAt }
      : { type, capturedAt };
  }

  private async buildNetworkErrorContext(error: any, operation: string, eventId?: string): Promise<Record<string, unknown>> {
    const networkContext = await this.getNetworkContext();
    return {
      operation,
      eventId: eventId || null,
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      networkContext,
      errorName: error?.name || null,
      status: typeof error?.status === 'number' ? error.status : null,
      statusText: error?.statusText || null,
      url: error?.url || null,
      message: error?.message || String(error),
      isTrusted: error?.error?.isTrusted === true
    };
  }

  /**
   * Sync device count to server (debounced ~1.5s per event to avoid cellular stampede)
   */
  async syncDeviceCount(eventId: string, deviceCount: number, totalEstimate: number, eventName?: string, eventPassword?: string, isCreator?: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const existing = this.pendingCountSyncs.get(eventId);
      if (existing) {
        existing.resolve();
      }

      this.pendingCountSyncs.set(eventId, {
        deviceCount,
        totalEstimate,
        eventName,
        eventPassword,
        isCreator,
        resolve,
        reject
      });

      const existingTimer = this.countSyncTimers.get(eventId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        this.countSyncTimers.delete(eventId);
        const pending = this.pendingCountSyncs.get(eventId);
        this.pendingCountSyncs.delete(eventId);
        if (!pending) {
          resolve();
          return;
        }
        this.flushDeviceCountSync(
          eventId,
          pending.deviceCount,
          pending.totalEstimate,
          pending.eventName,
          pending.eventPassword,
          pending.isCreator
        ).then(pending.resolve).catch(pending.reject);
      }, this.COUNT_SYNC_DEBOUNCE_MS);

      this.countSyncTimers.set(eventId, timer);
    });
  }

  private async flushDeviceCountSync(
    eventId: string,
    deviceCount: number,
    totalEstimate: number,
    eventName?: string,
    eventPassword?: string,
    isCreator?: boolean
  ): Promise<void> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();
      const networkContext = await this.getNetworkContext();

      const response = await firstValueFrom(
        this.http.post<CountSyncResponse>(
          `${SERVER_URL}/api/events/${eventId}/counts/sync`,
          {
            deviceId: this.deviceId,
            deviceCount,
            totalEstimate,
            deviceName: deviceInfo.deviceName,
            userDisplayName: deviceInfo.userDisplayName,
            platform: deviceInfo.platform,
            eventName: eventName,
            eventPassword: eventPassword,
            isCreator: !!isCreator,
            networkContext
          }
        )
      );

      return response as any;
    } catch (error: any) {
      this.debugLogger.error('ServerSync', 'Failed to sync device count:', error);

      await this.queueAction({
        type: 'count',
        eventId,
        deviceId: this.deviceId,
        deviceCount,
        totalEstimate,
        eventPassword,
        isCreator,
        timestamp: Date.now()
      });

      throw error;
    }
  }

  /**
   * Sync QR ticket scans to server
   */
  async syncTicketScans(
    eventId: string,
    scans: TicketScan[],
    deviceCount?: number,
    eventName?: string,
    eventPassword?: string,
    isCreator?: boolean,
    surfaceConflicts: boolean = true
  ): Promise<TicketSyncResponse> {
    if (!(await this.ensureServerSyncAccess('syncTicketScans'))) {
      return { success: false, processed: 0, conflicts: [] };
    }

    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();
      const networkContext = await this.getNetworkContext();
      const response = await firstValueFrom(
        this.http.post<TicketSyncResponse>(
          `${SERVER_URL}/api/events/${eventId}/tickets/sync`,
          {
            deviceId: this.deviceId,
            scans,
            deviceCount: typeof deviceCount === 'number' ? deviceCount : undefined,
            deviceName: deviceInfo.deviceName,
            userDisplayName: deviceInfo.userDisplayName,
            platform: deviceInfo.platform,
            eventName,
            eventPassword,
            isCreator: !!isCreator,
            networkContext
          }
        )
      );

      response.conflicts = this.filterDisplayableConflicts(await this.enrichConflicts(response.conflicts));

      // Only live scan submissions should surface conflict UI. Reconciliation,
      // background sync, and queued replays should sync state quietly.
      if (surfaceConflicts && response.conflicts.length > 0) {
        await this.showConflictNotification(eventId, eventName, response.conflicts);
      }

      return response;
    } catch (error: any) {
      this.debugLogger.error('ServerSync', 'Failed to sync ticket scans:', error);
      
      // Queue for later
      await this.queueAction({
        type: 'tickets',
        eventId,
        deviceId: this.deviceId,
        scans,
        deviceCount: typeof deviceCount === 'number' ? deviceCount : undefined,
        eventPassword,
        isCreator,
        timestamp: Date.now()
      });
      
      throw error;
    }
  }

  /**
   * Get accurate total count from server
   */
  async getEventCounts(eventId: string): Promise<EventCountsResponse> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    if (!(await this.ensureServerSyncAccess('getEventCounts'))) {
      return { success: false, total: 0, deviceCounts: [], lastUpdated: Date.now() };
    }

    try {
      const response = await firstValueFrom(
        this.http.get<EventCountsResponse>(`${SERVER_URL}/api/events/${eventId}/counts`, {
          params: { deviceId: this.deviceId || '' }
        })
      );

      return response;
    } catch (error: any) {
      this.debugLogger.error('ServerSync', 'Failed to get event counts:', error);
      throw error;
    }
  }

  /**
   * Get all tickets for an event from server.
   * Aggregates raw scans into per-ticket latest state if the server
   * doesn't provide a pre-built `tickets` array.
   */
  async getEventTickets(eventId: string): Promise<EventTicketsResponse> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    if (!(await this.ensureServerSyncAccess('getEventTickets'))) {
      return { success: false, tickets: [] };
    }

    try {
      const response = await firstValueFrom(
        this.http.get<EventTicketsResponse>(`${SERVER_URL}/api/events/${eventId}/tickets`, {
          params: { deviceId: this.deviceId || '' }
        })
      );

      // If the server already provides aggregated tickets, use them directly.
      if (response.tickets && response.tickets.length > 0) {
        return response;
      }

      // Otherwise aggregate from raw scans (current deployed server format).
      if (response.scans && response.scans.length > 0) {
        const latestByTicket = new Map<string, typeof response.scans[0]>();
        for (const scan of response.scans) {
          const existing = latestByTicket.get(scan.ticket_id);
          if (!existing || scan.scan_timestamp > existing.scan_timestamp) {
            latestByTicket.set(scan.ticket_id, scan);
          }
        }
        response.tickets = Array.from(latestByTicket.values()).map(s => ({
          ticketId: s.ticket_id,
          status: (s.action === 'scan_in' ? 'present' : 'absent') as 'present' | 'absent',
          deviceId: s.device_id,
          timestamp: s.scan_timestamp,
          hasConflict: s.conflict_flag === 1
        }));
      } else {
        response.tickets = [];
      }

      return response;
    } catch (error: any) {
      this.debugLogger.error('ServerSync', 'Failed to get event tickets:', error);
      throw error;
    }
  }

  /**
   * Get accurate count from server for QR ticket events
   * Server calculates count based on tickets with last action = 'scan_in'
   */
  async getServerCount(eventId: string): Promise<number | null> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<{
          eventId: string;
          presentCount?: number;
          totalCount?: number;
          totalScanned?: number;
          timestamp: number;
        }>(`${SERVER_URL}/api/events/${eventId}/count`, {
          params: { deviceId: this.deviceId || '' }
        })
      );

      const serverCount = response.totalCount ?? response.presentCount;
      return typeof serverCount === 'number' && Number.isFinite(serverCount) ? serverCount : null;
    } catch (error: any) {
      this.debugLogger.error(
        'ServerSync',
        `Failed to get server count for ${eventId}`,
        await this.buildNetworkErrorContext(error, 'getServerCount', eventId)
      );
      return null;
    }
  }

  async getEventAdminToken(eventId: string, password: string): Promise<string | null> {
    const result = await this.getEventAdminTokenResult(eventId, password);
    return result.token || null;
  }

  async getEventAdminTokenResult(
    eventId: string,
    password: string
  ): Promise<{ token?: string; status?: number; message?: string }> {
    if (!(await this.ensureServerSyncAccess('getEventAdminTokenResult'))) {
      return {
        status: 402,
        message: 'Complete plan required'
      };
    }

    try {
      if (!this.deviceId) {
        const info = await this.deviceService.getDeviceInfo();
        this.deviceId = info.deviceId;
      }
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; token?: string; message?: string }>(
          `${SERVER_URL}/api/events/${eventId}/admin-token`,
          { deviceId: this.deviceId, password }
        )
      );
      return { token: response?.token, message: response?.message };
    } catch (error: any) {
      this.debugLogger.error('ServerSync', `Failed to get admin token for ${eventId}`, error);
      return {
        status: error?.status,
        message: error?.error?.message || error?.message || 'Request failed'
      };
    }
  }

  async setEventPassword(eventId: string, password: string): Promise<boolean> {
    if (!(await this.ensureServerSyncAccess('setEventPassword'))) {
      return false;
    }

    try {
      if (!this.deviceId) {
        const info = await this.deviceService.getDeviceInfo();
        this.deviceId = info.deviceId;
      }
      await firstValueFrom(
        this.http.post(
          `${SERVER_URL}/api/events/${eventId}/set-password`,
          { deviceId: this.deviceId, password }
        )
      );
      return true;
    } catch (error: any) {
      this.debugLogger.error('ServerSync', `Failed to set password for ${eventId}`, error);
      return false;
    }
  }

  /**
   * Update test mode flag for an event (creator-only on server)
   */
  async setEventTestMode(eventId: string, enabled: boolean, eventPassword?: string): Promise<void> {
    if (!(await this.ensureServerSyncAccess('setEventTestMode'))) {
      return;
    }

    if (!this.deviceId) {
      await this.initializeDeviceId();
    }
    if (!this.deviceId) {
      throw new Error('Device ID unavailable');
    }

    try {
      await firstValueFrom(
        this.http.post(
          `${SERVER_URL}/api/events/${eventId}/test-mode`,
          {
            deviceId: this.deviceId,
            password: eventPassword,
            enabled
          }
        )
      );
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to update event test mode', error);
      throw error;
    }
  }

  async getEventCreatorDeviceId(eventId: string, eventPassword?: string): Promise<string | null> {
    if (!(await this.ensureServerSyncAccess('getEventCreatorDeviceId'))) {
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; creatorDeviceId?: string }>(
          `${SERVER_URL}/api/events/${eventId}/creator`,
          { password: eventPassword }
        )
      );
      if (response?.success) {
        return response.creatorDeviceId || null;
      }
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to fetch creator device id', error);
    }
    return null;
  }

  getServerUrl(): string {
    return SERVER_URL;
  }

  async getCurrentDeviceId(): Promise<string> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }
    return this.deviceId || '';
  }

  async registerQrEventAccessRequest(payload: {
    eventId: string;
    qrSignature: string;
    eventName: string;
    eventDate: string;
    ticketFormat: string;
    venueCode?: string;
    zoneCode?: string;
  }): Promise<QrAccessRequestResponse | null> {
    if (!(await this.ensureServerSyncAccess('registerQrEventAccessRequest'))) {
      return null;
    }

    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();
      const response = await firstValueFrom(
        this.http.post<QrAccessRequestResponse>(`${SERVER_URL}/api/qr-events/access-request`, {
          deviceId: this.deviceId,
          eventId: payload.eventId,
          qrSignature: payload.qrSignature,
          eventName: payload.eventName,
          eventDate: payload.eventDate,
          ticketFormat: payload.ticketFormat,
          venueCode: payload.venueCode || '',
          zoneCode: payload.zoneCode || '',
          deviceName: deviceInfo.deviceName,
          userDisplayName: deviceInfo.userDisplayName
        })
      );
      return response || null;
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to register QR event access request', error);
      return null;
    }
  }

  async suggestQRMappings(fingerprint: string, limit = 10): Promise<CustomQRTemplate[]> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    if (!(await this.ensureServerSyncAccess('suggestQRMappings'))) {
      return [];
    }

    try {
      const response = await firstValueFrom(
        this.http.get<QrMappingsResponse>(
          `${SERVER_URL}/api/qr-mappings/suggest`,
          { params: { fingerprint, limit: String(limit), deviceId: this.deviceId || '' } }
        )
      );
      return response?.mappings || [];
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to suggest QR mappings', error);
      return [];
    }
  }

  async getEventQRMappings(eventId: string): Promise<CustomQRTemplate[]> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    if (!(await this.ensureServerSyncAccess('getEventQRMappings'))) {
      return [];
    }

    try {
      const response = await firstValueFrom(
        this.http.get<QrMappingsResponse>(`${SERVER_URL}/api/events/${eventId}/qr-mappings`, {
          params: { deviceId: this.deviceId || '' }
        })
      );
      return response?.mappings || [];
    } catch (error) {
      this.debugLogger.error('ServerSync', `Failed to load event mappings for ${eventId}`, error);
      return [];
    }
  }

  async saveEventQRMapping(eventId: string, template: CustomQRTemplate): Promise<{ mappingId?: string; mappings: CustomQRTemplate[] }> {
    if (!(await this.ensureServerSyncAccess('saveEventQRMapping'))) {
      return { mappings: [] };
    }

    if (!this.deviceId) {
      await this.initializeDeviceId();
    }
    const response = await firstValueFrom(
      this.http.post<QrMappingsResponse>(`${SERVER_URL}/api/events/${eventId}/qr-mappings`, {
        deviceId: this.deviceId,
        template
      })
    );
    return { mappingId: response.mappingId, mappings: response.mappings || [] };
  }

  async recordQRMappingProof(mappingId: string, increment = 1): Promise<CustomQRTemplate | null> {
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    if (!(await this.ensureServerSyncAccess('recordQRMappingProof'))) {
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<QrMappingProofResponse>(`${SERVER_URL}/api/qr-mappings/${mappingId}/proof`, {
          increment,
          deviceId: this.deviceId
        })
      );
      return response?.mapping || null;
    } catch (error) {
      this.debugLogger.error('ServerSync', `Failed to record mapping proof for ${mappingId}`, error);
      await this.queueAction({
        type: 'qr_mapping_proof',
        eventId: 'shared',
        mappingId,
        increment,
        timestamp: Date.now()
      });
      return null;
    }
  }

  async publishQRMapping(mappingId: string): Promise<CustomQRTemplate | null> {
    if (!(await this.ensureServerSyncAccess('publishQRMapping'))) {
      return null;
    }

    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    try {
      const response = await firstValueFrom(
        this.http.post<QrMappingProofResponse>(`${SERVER_URL}/api/qr-mappings/${mappingId}/publish`, {
          deviceId: this.deviceId
        })
      );
      return response?.mapping || null;
    } catch (error) {
      this.debugLogger.error('ServerSync', `Failed to publish mapping ${mappingId}`, error);
      await this.queueAction({
        type: 'qr_mapping_publish',
        eventId: 'shared',
        mappingId,
        timestamp: Date.now()
      });
      return null;
    }
  }

  async updateAdminQRMapping(mappingId: string, updates: Partial<CustomQRTemplate>, token?: string, eventId?: string): Promise<CustomQRTemplate | null> {
    if (!(await this.ensureServerSyncAccess('updateAdminQRMapping'))) {
      return null;
    }

    try {
      const basePath = eventId
        ? `${SERVER_URL}/admin/api/events/${eventId}/qr-mappings/${mappingId}`
        : `${SERVER_URL}/admin/api/qr-mappings/${mappingId}`;
      const response = await firstValueFrom(
        this.http.put<{ success: boolean; mapping?: CustomQRTemplate }>(
          `${basePath}${token ? `?token=${encodeURIComponent(token)}` : ''}`,
          updates
        )
      );
      return response?.mapping || null;
    } catch (error) {
      this.debugLogger.error('ServerSync', `Failed to update admin mapping ${mappingId}`, error);
      return null;
    }
  }

  async deleteAdminQRMapping(mappingId: string, token?: string, eventId?: string): Promise<boolean> {
    if (!(await this.ensureServerSyncAccess('deleteAdminQRMapping'))) {
      return false;
    }

    try {
      const basePath = eventId
        ? `${SERVER_URL}/admin/api/events/${eventId}/qr-mappings/${mappingId}`
        : `${SERVER_URL}/admin/api/qr-mappings/${mappingId}`;
      await firstValueFrom(
        this.http.delete(
          `${basePath}${token ? `?token=${encodeURIComponent(token)}` : ''}`
        )
      );
      return true;
    } catch (error) {
      this.debugLogger.error('ServerSync', `Failed to delete admin mapping ${mappingId}`, error);
      return false;
    }
  }

  async getAdminQRMappings(): Promise<CustomQRTemplate[]> {
    if (!(await this.ensureServerSyncAccess('getAdminQRMappings'))) {
      return [];
    }

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; mappings: CustomQRTemplate[] }>(`${SERVER_URL}/admin/api/qr-mappings`)
      );
      return response?.mappings || [];
    } catch (error) {
      this.debugLogger.error('ServerSync', 'Failed to load admin mappings', error);
      return [];
    }
  }

  /**
   * Notify server of event deletion
   */
  async recordEventDeletion(eventId: string): Promise<boolean> {
    if (!(await this.ensureServerSyncAccess('recordEventDeletion'))) {
      return false;
    }

    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    try {
      const response = await firstValueFrom(
        this.http.delete<DeletionResponse>(
          `${SERVER_URL}/api/events/${eventId}/device/${this.deviceId}`
        )
      );

      return response.eventDeleted;
    } catch (error: any) {
      this.debugLogger.error('ServerSync', 'Failed to record event deletion:', error);
      
      // Queue for later
      await this.queueAction({
        type: 'deletion',
        eventId,
        deviceId: this.deviceId,
        timestamp: Date.now()
      });
      
      throw error;
    }
  }

  /**
   * Process all queued actions
   */
  async processQueue(): Promise<void> {
    if (this.isSyncing() || this.syncQueue().length === 0) {
      return;
    }

    this.isSyncing.set(true);
    const queue = [...this.syncQueue()];
    const failedActions: QueuedAction[] = [];

    for (const action of queue) {
      try {
        if (action.type === 'count') {
          await this.syncDeviceCount(
            action.eventId,
            action.deviceCount!,
            action.totalEstimate!,
            undefined,
            action.eventPassword,
            action.isCreator
          );
        } else if (action.type === 'tickets') {
          await this.syncTicketScans(action.eventId, action.scans!, action.deviceCount, undefined, action.eventPassword, action.isCreator, false);
        } else if (action.type === 'deletion') {
          await this.recordEventDeletion(action.eventId);
        } else if (action.type === 'qr_mapping_proof') {
          await this.recordQRMappingProof(action.mappingId!, action.increment || 1);
        } else if (action.type === 'qr_mapping_publish') {
          await this.publishQRMapping(action.mappingId!);
        }
        
      } catch (error) {
        this.debugLogger.error('ServerSync', `Failed to process ${action.type} action:`, error);
        failedActions.push(action);
      }
    }

    // Update queue with only failed actions
    this.syncQueue.set(failedActions);
    await this.saveQueue();

    this.isSyncing.set(false);
  }

  /**
   * Show notification when count is corrected
   */
  async showSyncNotification(oldCount: number, newCount: number): Promise<void> {
    try {
      const message = await firstValueFrom(
        this.translateService.get('SYNC.COUNT_SYNCED', { oldCount, newCount })
      );

      const toast = await this.toastController.create({
        message: message || `Count synchronized: ${oldCount} → ${newCount}`,
        duration: 4000,
        position: 'top',
        color: 'success',
        icon: 'checkmark-circle-outline'
      });

      await toast.present();
    } catch {
      // Swallow toast errors silently to avoid log noise
    }
  }

  /**
   * Show notification for ticket conflicts
   */
  private async showConflictNotification(eventId: string, eventName: string | undefined, conflicts: TicketConflict[]): Promise<void> {
    try {
      const modal = await this.modalController.create({
        component: ConflictsModalComponent,
        componentProps: {
          eventId,
          eventName,
          conflicts
        }
      });
      await modal.present();
    } catch {
      // Swallow modal errors silently to avoid log noise
    }
  }

  private async enrichConflicts(conflicts: TicketConflict[]): Promise<TicketConflict[]> {
    if (!conflicts.length) {
      return conflicts;
    }

    let deviceInfo: Awaited<ReturnType<DeviceService['getDeviceInfo']>> | null = null;

    try {
      deviceInfo = await this.deviceService.getDeviceInfo();
    } catch {
      deviceInfo = null;
    }

    return conflicts.map((conflict) => {
      const thisDevice = this.normalizeConflictScan(
        conflict.thisDevice,
        conflict.deviceId,
        conflict.action,
        undefined,
        deviceInfo
      );
      const otherScans = (conflict.otherScans?.length ? conflict.otherScans : [
        this.normalizeConflictScan(
          undefined,
          conflict.conflictWithDevice,
          conflict.existingAction,
          conflict.existingTimestamp,
          deviceInfo
        )
      ]).filter((scan): scan is ConflictDeviceScan => !!scan?.deviceId);

      return {
        ...conflict,
        thisDevice,
        otherScans
      };
    });
  }

  private filterDisplayableConflicts(conflicts: TicketConflict[]): TicketConflict[] {
    return conflicts.filter((conflict) => {
      const thisDeviceId = conflict.thisDevice?.deviceId || conflict.deviceId;
      const otherDeviceId = conflict.otherScans?.[0]?.deviceId || conflict.conflictWithDevice;

      // Suppress self-conflicts caused by duplicate retries or stale server-side
      // duplicate detection. A conflict modal should only describe mismatches
      // between different devices or distinct records.
      if (thisDeviceId && otherDeviceId && thisDeviceId === otherDeviceId) {
        this.debugLogger.warn('ServerSync', 'Suppressing self-conflict from server response.', {
          ticketId: conflict.ticketId,
          deviceId: thisDeviceId,
          reasonCode: conflict.reasonCode || null
        });
        return false;
      }

      return true;
    });
  }

  private normalizeConflictScan(
    scan: ConflictDeviceScan | undefined,
    fallbackDeviceId: string | undefined,
    fallbackAction: string | undefined,
    fallbackTimestamp: number | undefined,
    localDeviceInfo: Awaited<ReturnType<DeviceService['getDeviceInfo']>> | null
  ): ConflictDeviceScan | undefined {
    const deviceId = scan?.deviceId || fallbackDeviceId;
    if (!deviceId) {
      return undefined;
    }

    const isLocalDevice = !!localDeviceInfo && localDeviceInfo.deviceId === deviceId;
    const fallbackUserDisplayName = isLocalDevice
      ? (localDeviceInfo.userDisplayName || localDeviceInfo.userName || localDeviceInfo.deviceName)
      : undefined;
    const fallbackDeviceName = isLocalDevice ? localDeviceInfo.deviceName : undefined;

    return {
      deviceId,
      action: scan?.action || fallbackAction,
      timestamp: scan?.timestamp ?? fallbackTimestamp,
      userDisplayName: scan?.userDisplayName || fallbackUserDisplayName,
      deviceName: scan?.deviceName || fallbackDeviceName
    };
  }

  /**
   * Get queue status
   */
  getQueueStatus() {
    return {
      queueSize: this.syncQueue().length,
      isSyncing: this.isSyncing()
    };
  }

  /**
   * Clear queue (for testing/debugging)
   */
  async clearQueue() {
    this.syncQueue.set([]);
    await this.saveQueue();
  }
}
